import React, { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  DialogContentText
} from '@mui/material';
import { GradeScale } from '../../types/gradeScaleType';
import gradeScaleApi from '../../apis/gradeScale';
import GradeEntryDialog from './GradeEntryDialog';
import { EditGradeProps } from './types/GradeScaleType';
import { DialogGridWrapper } from './styled';
import gradeEntriesColumns from './utils/gradeEntriesColumns';
import { SnackbarSeverityType } from '../../enums';
import DialogModal from '../../components/DialogModal';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import pallet from '../../layouts/pallet';
import { dataGridSx } from '../common/styled/DataGrid';

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const EditGradeScaleDialog: React.FC<EditGradeProps> = ({
  open,
  onClose,
  gradeScale,
  school,
  onSaved,
  showToast
}) => {
  const [scale, setScale] = useState<GradeScale>({ ...gradeScale });
  const [editGradeKey, setEditGradeKey] = useState<string | null>(null);
  const [deleteGrade, setDeleteGrade] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setScale(deepClone(gradeScale));
    }
  }, [gradeScale, open]);

  const handleCancel = () => {
    setScale(deepClone(gradeScale));
    onClose();
  };

  const handleSetGradeScale = (next: any) => {
    setScale((prev) =>
      deepClone(typeof next === 'function' ? next(prev) : next)
    );
  };

  const handleDeleteConfirm = () => {
    if (deleteGrade) {
      const copy: GradeScale = {
        ...scale,
        gradeScaling: { ...scale.gradeScaling }
      } as GradeScale;
      delete (copy.gradeScaling as any)[deleteGrade];
      setScale(copy);
      setDeleteGrade(null);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      const saved = await gradeScaleApi.saveGradeScales(school, scale);
      const updated: GradeScale = {
        ...scale,
        ...saved,
        id: (saved as any)?.id || (saved as any)?._id || scale.id
      };

      onSaved(updated);
      onClose();
      showToast &&
        showToast(
          'Grade scale updated successfully!',
          SnackbarSeverityType.success
        );
    } catch (error) {
      console.error('Error updating grade scale:', error);
      showToast &&
        showToast('Failed to update grade scale.', SnackbarSeverityType.error);
    } finally {
      setSaving(false);
    }
  };

  const rows = Object.entries(scale.gradeScaling).map(([key, value]) => ({
    id: key,
    grade: key,
    min: (value as any).min,
    max: (value as any).max
  }));

  const columns = gradeEntriesColumns(
    (key: string) => setEditGradeKey(key),
    (key: string) => setDeleteGrade(key)
  );

  return (
    <>
      <DialogModal
        open={open}
        onClose={handleCancel}
        title='View / Edit Grade'
        actions={
          <>
            <Button variant='contained' onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSaveChanges}
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                'Save Changes'
              )}
            </Button>
          </>
        }
      >
        <Box display='flex' justifyContent='flex-end' mt={2} mr={2} ml={2}>
          <Button
            variant='contained'
            onClick={() => setEditGradeKey('')}
            disabled={saving}
          >
            Add Grade
          </Button>
        </Box>
        <DialogGridWrapper>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
            sx={dataGridSx(pallet)}
          />
        </DialogGridWrapper>
      </DialogModal>

      {editGradeKey !== null && (
        <GradeEntryDialog
          open={editGradeKey !== null}
          onClose={() => setEditGradeKey(null)}
          existingKey={editGradeKey}
          gradeScale={scale}
          setGradeScale={handleSetGradeScale}
        />
      )}

      <DialogModal
        open={!!deleteGrade}
        onClose={() => setDeleteGrade(null)}
        title='Delete Grade'
        actions={
          <>
            <Button onClick={() => setDeleteGrade(null)} color='primary'>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color='primary' autoFocus>
              Delete
            </Button>
          </>
        }
      >
        <DialogContentText>
          Are you sure you want to delete grade "{deleteGrade}"?
        </DialogContentText>
      </DialogModal>
    </>
  );
};

export default EditGradeScaleDialog;
